import {
  Avatar,
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { INFOGRAPH_URL, STAGING_INFOGRAPH_URL } from 'constants/infograph';
import { USER_ACTION } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useUser } from 'modules/User';
import { useTranslation } from 'react-i18next';
import { isStagingDomain } from 'utils/environment';

import { LinkButton } from './LinkButton';
import { LinkButtonKey, handleLinkButtonMixpanelKey } from './LinkButton.helpers';

const AvatarStyled = styled(Avatar)`
  &:hover,
  &:focus {
    cursor: pointer;
    boxshadow: var(--vg-shadows-outline);
  }
`;

export const UserAvatar = () => {
  const { t } = useTranslation('common_navbar');

  const { fullName } = useUser();
  const [is_tooltip_open, setIsTooltipOpen] = useBoolean(false);
  const [is_popover_open, setIsPopoverOpen] = useBoolean(false);

  const handleOnClick = (id: LinkButtonKey) => {
    Mixpanel.track(USER_ACTION.eventName, {
      from: handleLinkButtonMixpanelKey(id),
    });
  };

  const label = t('navbar.account');

  return (
    <Box zIndex='navbar' ml={2}>
      <Tooltip hasArrow isOpen={is_tooltip_open && !is_popover_open} placement='bottom' label={label} bg='black'>
        <Box
          onMouseEnter={setIsTooltipOpen.on}
          onFocus={setIsTooltipOpen.on}
          onBlur={setIsTooltipOpen.off}
          onMouseLeave={setIsTooltipOpen.off}
        >
          <Popover
            isOpen={is_popover_open}
            placement='bottom-end'
            onClose={setIsPopoverOpen.off}
            closeOnBlur={true}
            closeOnEsc={true}
            isLazy
          >
            <PopoverTrigger>
              <AvatarStyled
                onClick={setIsPopoverOpen.toggle}
                as='button'
                size='sm'
                name={fullName}
                aria-label={label}
                data-testid='useravatar-button'
                bg='avatar.5'
                color='white'
              />
            </PopoverTrigger>
            <UserAvatarContent onClickHandler={handleOnClick} />
          </Popover>
        </Box>
      </Tooltip>
    </Box>
  );
};

const UserAvatarContent = ({ onClickHandler }: { onClickHandler: (id: LinkButtonKey) => void }) => {
  const { t } = useTranslation('common_navbar');
  const INFOGRAPH_REDIRECT_URL = isStagingDomain ? STAGING_INFOGRAPH_URL : INFOGRAPH_URL;
  const MY_DESIGNS_ID = 'my-designs';
  const MY_ACCOUNT_ID = 'my-account';
  const SIGN_OUT_ID = 'sign-out';

  return (
    <PopoverContent width='170px' boxShadow='md' padding='0'>
      <PopoverArrow />
      <Flex direction='column' data-testid='useravatar-menu'>
        <LinkButton id={MY_DESIGNS_ID} href={`${INFOGRAPH_REDIRECT_URL}/infographics`} onClick={onClickHandler}>
          {t('navbar.my-designs')}
        </LinkButton>
        <LinkButton id={MY_ACCOUNT_ID} href={`${INFOGRAPH_REDIRECT_URL}/account/profile`} onClick={onClickHandler}>
          {t('navbar.my-account')}
        </LinkButton>
        <LinkButton id={SIGN_OUT_ID} href={`${INFOGRAPH_REDIRECT_URL}/logout`} onClick={onClickHandler}>
          {t('navbar.sign-out')}
        </LinkButton>
      </Flex>
    </PopoverContent>
  );
};
